import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { workItemsApi } from '@/api/workItems'
import { useTenantKey } from './useTenantKey'
export type { BulkDeleteWorkItemsResponse } from '@/api/workItems'
import type {
  CreateWorkItemRequest,
  UpdateWorkItemRequest,
  AssignWorkItemRequest,
  UpdateWorkItemStatusRequest,
  BulkUpdateWorkItemStatusRequest,
  PaginationParams,
  WorkItemFilters,
  ImportAzureDevOpsWorkItemsRequest,
  PushWorkItemsRequest,
} from '@/types'

export function useWorkItems(projectId: string, pagination?: PaginationParams, filters?: WorkItemFilters) {
  const tenantKey = useTenantKey()
  const params = pagination || filters ? { ...pagination, ...filters } : undefined

  return useQuery({
    queryKey: tenantKey('projects', projectId, 'work-items', params),
    queryFn: () => workItemsApi.getAll(projectId, params),
    enabled: !!projectId,
    staleTime: 0,
  })
}

export function useWorkItem(projectId: string, workItemId: string | null) {
  const tenantKey = useTenantKey()
  return useQuery({
    queryKey: tenantKey('projects', projectId, 'work-items', workItemId!),
    queryFn: () => workItemsApi.getById(projectId, workItemId!),
    enabled: !!projectId && !!workItemId,
    staleTime: 0,
  })
}

export function useWorkItemByNumber(projectId: string, number: number | null) {
  const tenantKey = useTenantKey()
  return useQuery({
    queryKey: tenantKey('projects', projectId, 'work-items', 'by-number', number),
    queryFn: () => workItemsApi.getByNumber(projectId, number as number),
    enabled: !!projectId && number != null && number > 0,
    staleTime: 0,
    retry: false,
  })
}

export function useWorkItemStats(projectId: string) {
  const tenantKey = useTenantKey()
  return useQuery({
    queryKey: tenantKey('projects', projectId, 'work-items', 'stats'),
    queryFn: () => workItemsApi.getStats(projectId),
    enabled: !!projectId,
    staleTime: 0,
  })
}

export function useWorkItemEpics(projectId: string) {
  const tenantKey = useTenantKey()
  return useQuery({
    queryKey: tenantKey('projects', projectId, 'work-items', 'epics'),
    queryFn: () => workItemsApi.getEpics(projectId),
    enabled: !!projectId,
    staleTime: 0,
  })
}

export function useCreateWorkItem(projectId: string) {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()

  return useMutation({
    mutationFn: (request: CreateWorkItemRequest) => workItemsApi.create(projectId, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'work-items') })
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'work-items', 'stats') })
    },
  })
}

export function useUpdateWorkItem(projectId: string) {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateWorkItemRequest }) =>
      workItemsApi.update(projectId, id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'work-items') })
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'history') })
    },
  })
}

export function useAssignWorkItem(projectId: string) {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: AssignWorkItemRequest }) =>
      workItemsApi.assign(projectId, id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'work-items') })
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'history') })
    },
  })
}

export function useUpdateWorkItemStatus(projectId: string) {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateWorkItemStatusRequest }) =>
      workItemsApi.updateStatus(projectId, id, data),
    // Awaited and run on failure too: a conflict means another user won, and the row must show their
    // value before the mutation settles — otherwise the next click sends a stale expectedStatus.
    onSettled: () => Promise.all([
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'work-items') }),
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'work-items', 'stats') }),
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'history') }),
    ]),
  })
}

export function useBulkUpdateWorkItemStatus(projectId: string) {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()

  return useMutation({
    mutationFn: (request: BulkUpdateWorkItemStatusRequest) => workItemsApi.bulkUpdateStatus(projectId, request),
    onSettled: () => Promise.all([
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'work-items') }),
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'work-items', 'stats') }),
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'history') }),
    ]),
  })
}

export function useDeleteWorkItem(projectId: string) {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()

  return useMutation({
    mutationFn: (id: string) => workItemsApi.delete(projectId, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'work-items') })
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'work-items', 'stats') })
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'workflow-runs') })
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'chain-runs') })
    },
  })
}

export function useBulkDeleteWorkItems(projectId: string) {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()

  return useMutation({
    mutationFn: (ids: string[]) => workItemsApi.bulkDelete(projectId, ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'work-items') })
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'work-items', 'stats') })
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'work-items', 'epics') })
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'workflow-runs') })
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'chain-runs') })
    },
  })
}

export function useWorkItemTranslation(projectId: string, workItemId: string, languageCode: string) {
  const tenantKey = useTenantKey()
  return useQuery({
    queryKey: tenantKey('projects', projectId, 'work-items', workItemId, 'translations', languageCode),
    queryFn: () => workItemsApi.translate(projectId, workItemId, languageCode),
    enabled: !!projectId && !!workItemId && !!languageCode && languageCode !== 'en',
    staleTime: 0,
    gcTime: 0,
  })
}

export function useImportFromAzureDevOps(projectId: string) {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()

  return useMutation({
    mutationFn: (request: ImportAzureDevOpsWorkItemsRequest) => workItemsApi.importFromAzureDevOps(projectId, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'work-items') })
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'work-items', 'stats') })
    },
  })
}

export function usePushWorkItemsToDevOps(projectId: string) {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()

  return useMutation({
    mutationFn: (request: PushWorkItemsRequest) => workItemsApi.pushToDevOps(projectId, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'work-items') })
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'history') })
    },
  })
}

export function useAzureDevOpsProcessConfig(projectId: string, enabled: boolean) {
  const tenantKey = useTenantKey()

  return useQuery({
    queryKey: tenantKey('projects', projectId, 'work-items', 'azure-devops-process-config'),
    queryFn: () => workItemsApi.getAzureDevOpsProcessConfig(projectId),
    enabled: !!projectId && enabled,
    staleTime: 60 * 60 * 1000,
    retry: false,
  })
}

export function useWorkItemAzureDevOpsProjects(projectId: string, enabled: boolean) {
  const tenantKey = useTenantKey()

  return useQuery({
    queryKey: tenantKey('projects', projectId, 'work-items', 'azure-devops-projects'),
    queryFn: () => workItemsApi.getAzureDevOpsProjects(projectId),
    enabled: !!projectId && enabled,
    staleTime: 5 * 60 * 1000,
    retry: false,
  })
}

export function useWorkItemAzureDevOpsEpics(projectId: string, azureProject: string, enabled: boolean) {
  const tenantKey = useTenantKey()

  return useQuery({
    queryKey: tenantKey('projects', projectId, 'work-items', 'azure-devops-epics', azureProject),
    queryFn: () => workItemsApi.getAzureDevOpsEpics(projectId, azureProject),
    enabled: !!projectId && !!azureProject && enabled,
    staleTime: 0,
    retry: false,
  })
}

export function useWorkItemAzureDevOpsFeatures(projectId: string, azureProject: string, epicId: number | null, enabled: boolean) {
  const tenantKey = useTenantKey()

  return useQuery({
    queryKey: tenantKey('projects', projectId, 'work-items', 'azure-devops-features', azureProject, epicId),
    queryFn: () => workItemsApi.getAzureDevOpsFeatures(projectId, azureProject, epicId as number),
    enabled: !!projectId && !!azureProject && epicId != null && epicId > 0 && enabled,
    staleTime: 0,
    retry: false,
  })
}
